import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import './Dashboard.css';

function Dashboard() {
  const { user, token, logout } = useCustomerAuth();
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ totalPurchased: 0, totalUsed: 0, available: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

  const fetchPayments = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/customer/auth/payments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payments');
      }

      const data = await response.json();
      setPayments(data.payments);
      setSummary(data.summary);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getProductName = (productType) => {
    const names = {
      single_video: 'Single Video',
      video_3pack: '3-Video Pack',
      video_10pack: '10-Video Pack',
    };
    return names[productType] || productType;
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>My Dashboard</h1>
          <div className="header-actions">
            <Link to="/settings" className="btn-settings">
              Settings
            </Link>
            <button onClick={handleLogout} className="btn-logout">
              Log Out
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-main">
        {/* Welcome Section */}
        <div className="welcome-section">
          <h2>Welcome back, {user?.name}!</h2>
          <p className="user-email">{user?.email}</p>
        </div>

        {/* Credits Summary */}
        <div className="credits-summary">
          <div className="credit-card available">
            <div className="credit-icon">🎬</div>
            <div className="credit-info">
              <h3>{summary.available}</h3>
              <p>Available Credits</p>
            </div>
          </div>

          <div className="credit-card purchased">
            <div className="credit-icon">💳</div>
            <div className="credit-info">
              <h3>{summary.totalPurchased}</h3>
              <p>Total Purchased</p>
            </div>
          </div>

          <div className="credit-card used">
            <div className="credit-icon">✓</div>
            <div className="credit-info">
              <h3>{summary.totalUsed}</h3>
              <p>Videos Created</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions">
          <h3>Quick Actions</h3>
          <div className="action-buttons">
            <Link to="/daycare/default" className="action-btn create-video">
              <span className="action-icon">🎥</span>
              <span>Create New Video</span>
            </Link>
            {summary.available === 0 && (
              <Link to="/daycare/default" className="action-btn buy-credits">
                <span className="action-icon">💳</span>
                <span>Buy More Credits</span>
              </Link>
            )}
          </div>
        </div>

        {/* Payment History */}
        <div className="payment-history">
          <h3>Payment History</h3>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {payments.length === 0 ? (
            <div className="no-payments">
              <p>No payment history yet.</p>
              <Link to="/daycare/default" className="btn-get-started">
                Get Started
              </Link>
            </div>
          ) : (
            <div className="payments-table">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Package</th>
                    <th>Amount</th>
                    <th>Credits</th>
                    <th>Used</th>
                    <th>Remaining</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td>{formatDate(payment.paidAt || payment.createdAt)}</td>
                      <td>{getProductName(payment.productType)}</td>
                      <td className="amount">{formatCurrency(payment.amount)}</td>
                      <td>{payment.videosRemaining}</td>
                      <td>{payment.videosUsed}</td>
                      <td className="credits-left">
                        {payment.creditsLeft}
                      </td>
                      <td>
                        <span className={`status status-${payment.status}`}>
                          {payment.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
